import React from "react";
import { Link } from "@tanstack/react-router";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ALL_POSTS, type Post } from "@/data/library";
import { LibraryThumb } from "@/components/library/LibraryThumb";
import { TagChips } from "@/components/library/TagChips";
import { TypeTagRail } from "@/components/library/TypeTagRail";

const Header = () => (
  <header className="pt-40 md:pt-52 px-6 md:px-10">
    <div className="max-w-6xl mx-auto border-b border-border pb-12 md:pb-16">
      <div className="max-w-3xl">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Bigdelta Hub
        </span>
        <h1
          className="mt-6 text-5xl md:text-6xl leading-[1.05] tracking-tight"
          style={{ fontFamily: "var(--font-body)" }}
        >
          Everything, by topic.
        </h1>
        <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
          Blog posts, guides and metric pages in one place — pick a tag to see everything we have on
          it.
        </p>
      </div>
    </div>
  </header>
);

const PostCard = ({ post }: { post: Post }) => (
  <Link to={post.href} className="group flex flex-col">
    <div className="flex items-center justify-between mb-4">
      <time className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
        {post.date}
      </time>
      <TagChips tags={post.tags} />
    </div>
    <div className="mb-6 overflow-hidden rounded-sm">
      <LibraryThumb slug={post.slug} image={post.image} live />
    </div>
    <h2
      className="text-xl md:text-2xl leading-tight tracking-tight mb-3 group-hover:underline underline-offset-4 decoration-1"
      style={{ fontFamily: "var(--font-body)" }}
    >
      {post.title}
    </h2>
    <p className="text-muted-foreground leading-relaxed line-clamp-3">{post.dek}</p>
  </Link>
);

const ListPostItem = ({ post }: { post: Post }) => (
  <Link
    to={post.href}
    className="group grid grid-cols-[100px_1fr] md:grid-cols-[160px_1fr] lg:grid-cols-[180px_1fr] gap-4 md:gap-8 lg:gap-10 py-6 md:py-8 lg:py-10 border-b border-foreground last:border-b-0"
  >
    <div className="overflow-hidden rounded-sm">
      <LibraryThumb slug={post.slug} image={post.image} live />
    </div>
    <div className="flex flex-col justify-center">
      <span className="mb-2 md:mb-3 inline-flex self-start">
        <TagChips tags={post.tags} />
      </span>
      <h2
        className="text-lg md:text-xl lg:text-2xl leading-tight tracking-tight mb-2 md:mb-3 group-hover:underline underline-offset-4 decoration-1"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {post.title}
      </h2>
      <p className="text-sm md:text-base text-muted-foreground leading-relaxed line-clamp-2">
        {post.dek}
      </p>
    </div>
  </Link>
);

const Hub = () => {
  // Flat, newest-first list across every content type.
  const posts = ALL_POSTS.slice().sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        <Header />
        <TypeTagRail base="/hub" />
        <section className="px-6 md:px-10 py-16 md:py-20">
          <div className="max-w-6xl mx-auto">
            {posts.length === 0 ? (
              <p className="text-muted-foreground">No articles yet.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 lg:gap-x-12 md:gap-y-16">
                  {posts.slice(0, 3).map((post) => (
                    <PostCard key={post.slug} post={post} />
                  ))}
                </div>
                {posts.length > 3 && (
                  <div className="mt-16 md:mt-20">
                    {posts.slice(3).map((post) => (
                      <ListPostItem key={post.slug} post={post} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Hub;
